import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, UserCircle, Shield, ShieldCheck } from 'lucide-react';

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'STAFF' });
  const [editData, setEditData] = useState({ name: '', role: '' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) { setError('Name and email are required'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/users`, newUser, { withCredentials: true });
      setSuccess(`User created! Password sent to ${newUser.email}`);
      setNewUser({ name: '', email: '', role: 'STAFF' });
      setAddUserDialog(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/${selectedUser.id}`, editData, { withCredentials: true });
      setSuccess('User updated successfully');
      setEditUserDialog(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) return;
    setLoading(true);
    setError('');
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, { withCredentials: true });
      setSuccess('User deleted successfully');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, { is_active: !currentStatus }, { withCredentials: true });
      fetchUsers();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to toggle status');
    }
  };

  return (
    <div className="p-8" data-testid="users-content">
      {/* Page Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#121212]" style={{ fontFamily: 'Playfair Display, serif' }}>
            User Management
          </h1>
          <p className="text-sm text-[#808080] mt-1">Manage admin and staff accounts</p>
        </div>
        <Dialog open={addUserDialog} onOpenChange={setAddUserDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#C69C6D] hover:bg-[#B38B5D] text-black rounded-lg" data-testid="add-user-button">
              <Plus size={16} className="mr-1.5" />Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Full Name *</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="John Doe" className="mt-1.5" data-testid="new-user-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="john@example.com" className="mt-1.5" data-testid="new-user-email" />
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin (Full Access)</SelectItem>
                    <SelectItem value="STAFF">Staff (Limited Access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-700">
                A random password will be auto-generated and emailed to the user.
              </div>
              <Button onClick={handleAddUser} disabled={loading} className="w-full bg-[#C69C6D] hover:bg-[#B38B5D] text-black font-semibold" data-testid="submit-new-user">
                {loading ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5" data-testid="users-error">{error}<button onClick={() => setError('')} className="ml-3 underline text-sm">Dismiss</button></div>}
      {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg mb-5" data-testid="users-success">{success}</div>}

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-[#EBEBEB] shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FAFAF8]">
              <TableHead className="font-semibold text-xs uppercase tracking-wider">User</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider">Email</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider">Role</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#808080]">No users found.</TableCell></TableRow>
            )}
            {users.map((u) => (
              <TableRow key={u.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#C69C6D]/10 flex items-center justify-center flex-shrink-0">
                      {u.role === 'ADMIN' ? <ShieldCheck size={18} className="text-[#C69C6D]" /> : <UserCircle size={18} className="text-[#808080]" />}
                    </div>
                    <span className="font-medium text-[#121212]">{u.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-[#808080] text-sm">{u.email}</TableCell>
                <TableCell>
                  <Badge
                    className={u.role === 'ADMIN' ? 'bg-[#C69C6D]/15 text-[#C69C6D] border-[#C69C6D]/30 hover:bg-[#C69C6D]/20' : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'}
                    variant="outline"
                    data-testid={`user-role-${u.id}`}
                  >
                    {u.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => handleToggleActive(u.id, u.is_active)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                      u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                    data-testid={`toggle-status-${u.id}`}
                  >
                    {u.is_active ? 'Active' : 'Inactive'}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => { setSelectedUser(u); setEditData({ name: u.name, role: u.role }); setEditUserDialog(true); }}
                      className="p-2 rounded-lg hover:bg-[#F5F5F0] text-[#808080] hover:text-[#121212] transition-colors"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Pencil size={15} />
                    </button>
                    {u.email !== user?.email && (
                      <button
                        onClick={() => handleDeleteUser(u.id, u.name)}
                        className="p-2 rounded-lg hover:bg-red-50 text-[#808080] hover:text-red-500 transition-colors"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {selectedUser && (
            <div className="space-y-4 pt-2">
              <div>
                <Label>Full Name</Label>
                <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="mt-1.5" data-testid="edit-user-name" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editData.role} onValueChange={(value) => setEditData({ ...editData, role: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin (Full Access)</SelectItem>
                    <SelectItem value="STAFF">Staff (Limited Access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdateUser} disabled={loading} className="w-full bg-[#C69C6D] hover:bg-[#B38B5D] text-black font-semibold" data-testid="submit-edit-user">
                {loading ? 'Updating...' : 'Update User'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
